import { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { SessionState, PendingContributionItem } from '../../../services/participant.service';
import { AvatarCard } from '../components/AvatarCard';
import { ConverseCTA } from '../components/ConverseCTA';
import { FileText, ExternalLink, X, Play, Square } from 'lucide-react';

function ContributionFeed({ contributions }: { contributions: PendingContributionItem[] }) {
  const [playingKey, setPlayingKey] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  if (contributions.length === 0) return null;

  function toggleAudio(key: string, url: string) {
    if (playingKey === key) {
      audioRef.current?.pause();
      setPlayingKey(null);
      return;
    }
    if (audioRef.current) audioRef.current.pause();
    const audio = new Audio(url);
    audioRef.current = audio;
    audio.play();
    setPlayingKey(key);
    audio.onended = () => setPlayingKey(null);
  }

  return (
    <div className="px-4 mb-5">
      <p className="text-[var(--pt-text-dimmer)] text-xs uppercase tracking-widest mb-3">
        What's Happening · {contributions.length}
      </p>
      <div className="space-y-3">
        {contributions.map((c, i) => {
          const key = `${c.role}-${i}`;
          let urgencyColor: string;
          if (c.urgency === 'urgent') urgencyColor = 'text-red-400';
          else if (c.urgency === 'important') urgencyColor = 'text-yellow-400';
          else urgencyColor = 'text-primary';
          return (
            <div key={key} className="bg-[var(--pt-surface)] border border-[var(--pt-border)] rounded-2xl p-4">
              <div className="flex items-start gap-3">
                <div
                  className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-bold text-[var(--pt-text)] overflow-hidden"
                  style={{ backgroundColor: c.avatarPrimaryColor || '#334155' }}
                >
                  {c.avatarImageUrl
                    ? <img src={c.avatarImageUrl} alt="" className="w-8 h-8 object-cover" />
                    : (c.avatarName || '?').charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[var(--pt-text)] text-xs font-semibold">{c.avatarName}</span>
                    {c.urgency && (
                      <span className={`text-xs font-medium capitalize ${urgencyColor}`}>{c.urgency}</span>
                    )}
                  </div>
                  {c.reason && (
                    <p className="text-[var(--pt-text-dimmer)] text-xs mb-1.5 italic">{c.reason}</p>
                  )}
                  <p className="text-[var(--pt-text)] text-sm leading-relaxed">{c.text}</p>
                  {c.audioUrl && (
                    <button
                      onClick={() => toggleAudio(key, c.audioUrl!)}
                      className="mt-2 flex items-center gap-1.5 text-primary text-xs font-medium"
                    >
                      {playingKey === key
                        ? <><Square size={11} fill="currentColor" /> Stop</>
                        : <><Play size={11} fill="currentColor" /> Play audio</>}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface Props {
  state: SessionState;
  sessionId: string;
}

function isHtml(body: unknown): body is string {
  return typeof body === 'string' && body.trim().startsWith('<');
}

function bodyText(body: unknown): string {
  if (typeof body === 'string') return body;
  if (body && typeof body === 'object') return JSON.stringify(body);
  return '';
}

function MarkdownViewer({ url, title, onClose }: { url: string; title: string; onClose: () => void }) {
  const [content, setContent] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetch(url)
      .then(r => r.text())
      .then(setContent)
      .catch(() => setError(true));
  }, [url]);

  return (
    <div className="fixed inset-0 z-50 bg-[#0f2027] flex flex-col">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--pt-border)] shrink-0">
        <span className="text-[var(--pt-text)] font-semibold text-sm truncate pr-4">{title}</span>
        <button onClick={onClose} className="text-[var(--pt-text-dim)] hover:text-[var(--pt-text)] shrink-0"><X size={20} /></button>
      </div>
      <div className="flex-1 overflow-y-auto px-5 py-5">
        {error && <p className="text-red-400 text-sm">Failed to load content.</p>}
        {content && (
          <div className="prose prose-invert prose-sm max-w-none
            prose-table:border-collapse
            prose-td:border prose-td:border-[var(--pt-border-strong)] prose-td:px-3 prose-td:py-2
            prose-th:border prose-th:border-[var(--pt-border-strong)] prose-th:px-3 prose-th:py-2 prose-th:bg-[var(--pt-surface-strong)]
            prose-headings:text-[var(--pt-text)] prose-p:text-[var(--pt-text)] prose-li:text-[var(--pt-text)]">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
          </div>
        )}
        {!content && !error && <p className="text-[var(--pt-text-dimmer)] text-sm">Loading…</p>}
      </div>
    </div>
  );
}

export function SessionTab({ state, sessionId }: Props) {
  const [htmlViewer, setHtmlViewer] = useState<{ title: string; html?: string; url?: string; isMarkdown?: boolean } | null>(null);

  return (
    <div className="flex-1 overflow-y-auto pb-6">
      {/* Phase header */}
      <div className="px-4 pt-4 pb-3">
        <div className="flex items-center gap-2">
          <span className="text-[var(--pt-text-dim)] text-xs uppercase tracking-widest">Phase {state.phaseIndex + 1} of {state.totalPhases}</span>
          <span className="w-1 h-1 rounded-full bg-[var(--pt-surface-strong)]" />
          <span className="text-primary text-xs font-medium capitalize">{state.phase.replace(/_/g, ' ')}</span>
        </div>
      </div>

      {/* Avatar horizontal scroll */}
      <div className="px-4 mb-5">
        <p className="text-[var(--pt-text-dimmer)] text-xs uppercase tracking-widest mb-3">AI Participants</p>
        {state.avatars.length === 0 ? (
          <p className="text-[var(--pt-text-dimmer)] text-sm">No avatars in session yet</p>
        ) : (
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-none">
            {state.avatars.map(avatar => (
              <AvatarCard key={avatar.id} avatar={avatar} />
            ))}
          </div>
        )}
      </div>

      {/* Pending contributions feed */}
      <ContributionFeed contributions={state.pendingContributions ?? []} />

      {/* Published outputs */}
      {state.publishedOutputs.length > 0 && (
        <div className="px-4">
          <p className="text-[var(--pt-text-dimmer)] text-xs uppercase tracking-widest mb-3">Session Outputs</p>
          <div className="space-y-3">
            {state.publishedOutputs.map(output => {
              const hasUrl = !!output.url;
              const hasHtml = isHtml(output.body);
              const isMarkdown = hasUrl && (output.url!.includes('.md') || output.title?.toLowerCase().includes('markdown') || output.type === 'markdown');
              const viewable = hasUrl || hasHtml;
              const openViewer = viewable
                ? () => setHtmlViewer({ title: output.title, html: hasHtml ? output.body : undefined, url: hasUrl ? output.url : undefined, isMarkdown })
                : undefined;
              return (
                <div
                  key={output.id}
                  className={`bg-[var(--pt-surface)] border border-[var(--pt-border)] rounded-2xl p-4 ${viewable ? 'cursor-pointer active:bg-[var(--pt-surface-strong)]' : ''}`}
                  onClick={openViewer}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-primary/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                      <FileText size={14} className="text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs text-primary/80 font-medium uppercase tracking-wide">{output.type}</span>
                      </div>
                      <h3 className="text-[var(--pt-text)] font-semibold text-sm mb-1">{output.title}</h3>
                      {viewable ? (
                        <div className="flex items-center gap-1 text-primary text-xs font-medium mt-2">
                          <ExternalLink size={12} />
                          <span>Tap to view</span>
                        </div>
                      ) : (
                        <p className="text-[var(--pt-text-dim)] text-sm leading-relaxed">{bodyText(output.body)}</p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {state.publishedOutputs.length === 0 && state.avatars.length > 0 && (
        <div className="px-4">
          <div className="bg-[var(--pt-surface)] border border-dashed border-[var(--pt-border)] rounded-2xl p-6 text-center">
            <p className="text-[var(--pt-text-dimmer)] text-sm">Outputs will appear here when your facilitator publishes them</p>
          </div>
        </div>
      )}

      <ConverseCTA sessionId={sessionId} available={state.reachy.available} busy={state.reachy.busy} />

      {/* Fullscreen viewer */}
      {htmlViewer && htmlViewer.isMarkdown && htmlViewer.url && (
        <MarkdownViewer url={htmlViewer.url} title={htmlViewer.title} onClose={() => setHtmlViewer(null)} />
      )}
      {htmlViewer && !htmlViewer.isMarkdown && (
        <div className="fixed inset-0 z-50 bg-white flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 bg-[#0f2027] border-b border-[var(--pt-border)] shrink-0">
            <span className="text-[var(--pt-text)] font-semibold text-sm truncate pr-4">{htmlViewer.title}</span>
            <button onClick={() => setHtmlViewer(null)} className="text-[var(--pt-text-dim)] hover:text-[var(--pt-text)] shrink-0">
              <X size={20} />
            </button>
          </div>
          {htmlViewer.url ? (
            <iframe src={htmlViewer.url} className="flex-1 w-full border-0" title={htmlViewer.title} />
          ) : (
            <iframe srcDoc={htmlViewer.html} className="flex-1 w-full border-0" sandbox="allow-scripts allow-same-origin" title={htmlViewer.title} />
          )}
        </div>
      )}
    </div>
  );
}
